import React, { useState } from "react";

export interface ExpertIntroVideoProps {
  /** YouTube video ID (e.g. "hZ1b-GqR7KA" from https://www.youtube.com/watch?v=hZ1b-GqR7KA) */
  videoId?: string;
  /** Custom thumbnail URL. If not set, uses YouTube default (maxresdefault or hqdefault). */
  thumbnailUrl?: string;
  /** Optional title for accessibility */
  title?: string;
}

// Default values - set these here for a site-wide intro video when an expert has no videoId
const DEFAULT_VIDEO_ID = "";
const DEFAULT_THUMBNAIL_URL = "";

const ExpertIntroVideo: React.FC<ExpertIntroVideoProps> = ({
  videoId: propVideoId,
  thumbnailUrl: propThumbnailUrl,
  title = "Introduction video",
}) => {
  const videoId = propVideoId || DEFAULT_VIDEO_ID;
  const [playing, setPlaying] = useState(false);

  const thumbnailUrl =
    propThumbnailUrl ||
    DEFAULT_THUMBNAIL_URL ||
    (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : "");

  if (!videoId) return null;

  return (
    <div
      style={{
        width: "100%",
        marginBottom: 24,
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: "#032539",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingTop: "56.25%", // 16:9
          height: 0,
        }}
      >
        {playing ? (
          <iframe
            title={title}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              padding: 0,
              border: "none",
              cursor: "pointer",
              background: "none",
              display: "block",
            }}
            aria-label={`Play ${title}`}
          >
            <img
              src={thumbnailUrl}
              alt={title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                const target = e.currentTarget;
                if (videoId && !target.src.includes("hqdefault")) {
                  target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 68,
                height: 48,
                backgroundColor: "rgba(0,0,0,0.7)",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: "20px solid #fff",
                  borderTop: "12px solid transparent",
                  borderBottom: "12px solid transparent",
                  marginLeft: 6,
                }}
              />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpertIntroVideo;
